import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import SEO from '../components/SEO';
import HeroSection from '../components/HeroSection';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HERO_IMG = '/images/heritage-opbanham.jpg';

const EMPTY = {
  organisation_name: '',
  organisation_address: '',
  organisation_type: '',
  charity_number: '',
  has_other_funders: false,
  other_funders_detail: '',
  other_funders_amount: '',
  match_funding: false,
  amount_requested: '',
  project_duration: '',
  total_funding_required: '',
  project_benefit: '',
  agree_to_promote: false,
  promotion_detail: '',
  need_branding_assets: false,
  contact_name: '',
  contact_email: '',
  contact_phone: '',
  volunteer_heritage: false,
  volunteer_glossfest: false,
  volunteer_bonfire: false,
  bank_name: '',
  account_name: '',
  account_number: '',
  sort_code: '',
  referral_name: '',
  referral_age: '',
  referral_email: '',
  referral_phone: '',
  retain_data: false,
};

function FieldLabel({ children, required }) {
  return (
    <Label className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
      {children}
      {required && <span className="text-[#D62F2F] ml-1">*</span>}
    </Label>
  );
}

function SectionHeader({ eyebrow, title, blurb }) {
  return (
    <div className="border-l-4 border-[#F0A500] pl-6 mb-8">
      <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-2">{eyebrow}</span>
      <h3 className="text-2xl font-bold text-[#0A0A0A]">{title}</h3>
      {blurb && <p className="text-sm text-[#52525B] mt-2 max-w-2xl">{blurb}</p>}
    </div>
  );
}

function ClosedState() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-[#F4F4F5] px-4 py-20" data-testid="donation-closed-state">
      <div className="max-w-xl text-center">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#F0A500] block mb-4">
          Donation Requests
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#0A0A0A] mb-4">
          Currently closed
        </h1>
        <p className="text-base text-[#52525B] leading-relaxed mb-8">
          We run donation rounds twice a year, with the next window opening shortly. Pop your details on a sticky note for when we reopen, or get in touch directly at{' '}
          <a href="mailto:donations@glossoproundtable.org" className="text-[#F0A500] hover:underline font-semibold">
            donations@glossoproundtable.org
          </a>
          {' '}— especially if your project is time-sensitive.
        </p>
        <Link
          to="/"
          data-testid="donation-back-home-btn"
          className="inline-block px-6 py-3 bg-[#F0A500] hover:bg-[#d99400] text-[#0A0A0A] font-bold uppercase tracking-wider text-sm transition-colors"
        >
          Back to Home
        </Link>
      </div>
    </div>
  );
}

export default function DonationRequestPage() {
  const [statusChecked, setStatusChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [notRobot, setNotRobot] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/donations/status`);
        if (!cancelled) setOpen(Boolean(data.donations_open));
      } catch {
        if (!cancelled) setOpen(false);
      } finally {
        if (!cancelled) setStatusChecked(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const update = (key) => (e) => {
    const value = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [key]: value }));
  };
  const updateBool = (key) => (checked) => setForm((f) => ({ ...f, [key]: checked === true }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      const payload = { ...form };
      payload.referral_age = payload.referral_age ? Number(payload.referral_age) : null;
      // Trim empty optional fields so the API gets clean nulls
      ['charity_number','other_funders_detail','other_funders_amount','project_duration',
       'promotion_detail','bank_name','account_name','account_number','sort_code',
       'referral_name','referral_email','referral_phone'].forEach((k) => {
        if (!payload[k]) payload[k] = null;
      });
      await axios.post(`${API}/donation-request`, payload);
      setSubmitted(true);
      setForm(EMPTY);
      setNotRobot(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Donation requests are currently closed.');
        setOpen(false);
      } else if (err.response?.data?.detail) {
        const d = err.response.data.detail;
        setError(typeof d === 'string' ? d : 'Please check the required fields and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!statusChecked) {
    return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">Loading…</div>;
  }

  if (!open) return <ClosedState />;

  return (
    <div data-testid="donation-request-page">
      <SEO
        title="Donation Request"
        description="Apply for funding from Glossop & District Round Table 916. We support local charities, community projects and good causes across the Glossop area."
        path="/donation-request"
      />
      <HeroSection
        image={HERO_IMG}
        title="Donation Request"
        subtitle="Apply for funding from Glossop & District Round Table"
        fullHeight={false}
        alt="Glossop Round Table volunteers alongside Operation Banham veterans at their Veterans Living History Museum display during Heritage Weekend"
        captionTag="Heritage Weekend 2026"
      />

      <section className="py-16 sm:py-20 bg-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          {submitted ? (
            <div data-testid="donation-success-state" className="p-8 border-l-4 border-[#F0A500] bg-[#FEF7E6]">
              <h2 className="text-2xl sm:text-3xl font-bold text-[#0A0A0A] mb-3">Thanks — your request is in</h2>
              <p className="text-base text-[#52525B] leading-relaxed mb-4">
                We&rsquo;ve received your donation request and a confirmation email has been sent to our donations team. We&rsquo;ll be in touch once your application has been reviewed. Donations are generally made during Q4 of the financial year &mdash; if your project is time-sensitive, let us know and we&rsquo;ll prioritise.
              </p>
              <Link to="/" className="text-sm font-bold uppercase tracking-wide text-[#F0A500] hover:text-[#D99500]">
                Back to Home &rarr;
              </Link>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-12" data-testid="donation-form">
              <div className="bg-[#FEF7E6] border-l-4 border-[#F0A500] p-5 text-sm text-[#52525B] leading-relaxed">
                Please complete the form below. Fields marked with <span className="text-[#D62F2F] font-bold">*</span> are required. All information is treated in line with our{' '}
                <Link to="/policies/data-protection" className="text-[#F0A500] hover:underline font-semibold">data protection policy</Link>.
              </div>

              {/* Organisation */}
              <div>
                <SectionHeader eyebrow="Step 1" title="About your organisation" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel required>Organisation name</FieldLabel>
                    <Input data-testid="donation-organisation_name" required value={form.organisation_name} onChange={update('organisation_name')} />
                  </div>
                  <div>
                    <FieldLabel required>Type of organisation</FieldLabel>
                    <Input data-testid="donation-organisation_type" required placeholder="e.g. Registered charity, community group" value={form.organisation_type} onChange={update('organisation_type')} />
                  </div>
                  <div className="sm:col-span-2">
                    <FieldLabel required>Organisation address</FieldLabel>
                    <Input data-testid="donation-organisation_address" required value={form.organisation_address} onChange={update('organisation_address')} />
                  </div>
                  <div>
                    <FieldLabel>Registered charity number (if applicable)</FieldLabel>
                    <Input data-testid="donation-charity_number" value={form.charity_number} onChange={update('charity_number')} />
                  </div>
                </div>
              </div>

              {/* Funding */}
              <div>
                <SectionHeader eyebrow="Step 2" title="The funding request" blurb="Tell us what you're asking for, what it'll achieve, and whether anyone else is helping fund the project." />
                <div className="space-y-5">
                  <label className="flex items-start gap-3 p-4 bg-[#F4F4F5]" data-testid="donation-has_other_funders-wrap">
                    <Checkbox checked={form.has_other_funders} onCheckedChange={updateBool('has_other_funders')} />
                    <span className="text-sm text-[#0A0A0A]">We have other funders supporting this project</span>
                  </label>
                  {form.has_other_funders && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pl-4 border-l-2 border-[#F0A500]">
                      <div className="sm:col-span-2">
                        <FieldLabel>Who are they, and what are they funding?</FieldLabel>
                        <Input data-testid="donation-other_funders_detail" value={form.other_funders_detail} onChange={update('other_funders_detail')} />
                      </div>
                      <div>
                        <FieldLabel>How much are they donating?</FieldLabel>
                        <Input data-testid="donation-other_funders_amount" placeholder="e.g. £1,000" value={form.other_funders_amount} onChange={update('other_funders_amount')} />
                      </div>
                      <div>
                        <FieldLabel>Are they match-funding?</FieldLabel>
                        <label className="flex items-center gap-3 mt-2">
                          <Checkbox checked={form.match_funding} onCheckedChange={updateBool('match_funding')} data-testid="donation-match_funding" />
                          <span className="text-sm">Yes, they are match-funding our donation</span>
                        </label>
                      </div>
                    </div>
                  )}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <FieldLabel required>Amount requested from us</FieldLabel>
                      <Input data-testid="donation-amount_requested" required placeholder="e.g. £500" value={form.amount_requested} onChange={update('amount_requested')} />
                    </div>
                    <div>
                      <FieldLabel>Project / scheme duration</FieldLabel>
                      <Input data-testid="donation-project_duration" placeholder="e.g. 3 months, ongoing" value={form.project_duration} onChange={update('project_duration')} />
                    </div>
                    <div className="sm:col-span-2">
                      <FieldLabel required>Total funding required for the project</FieldLabel>
                      <Input data-testid="donation-total_funding_required" required placeholder="e.g. £2,500 total project cost" value={form.total_funding_required} onChange={update('total_funding_required')} />
                    </div>
                    <div className="sm:col-span-2">
                      <FieldLabel required>How will the funding benefit your organisation / community?</FieldLabel>
                      <textarea
                        data-testid="donation-project_benefit"
                        required
                        rows={5}
                        value={form.project_benefit}
                        onChange={update('project_benefit')}
                        className="w-full bg-white border border-zinc-300 px-3 py-2 text-sm focus:border-[#F0A500] focus:outline-none focus:ring-1 focus:ring-[#F0A500]"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Promotion */}
              <div>
                <SectionHeader eyebrow="Step 3" title="Promotion &amp; recognition" blurb="We use our donations to help raise our profile in the community. Tell us how you'll spread the word." />
                <div className="space-y-5">
                  <label className="flex items-start gap-3 p-4 bg-[#F4F4F5]" data-testid="donation-agree_to_promote-wrap">
                    <Checkbox checked={form.agree_to_promote} onCheckedChange={updateBool('agree_to_promote')} required />
                    <span className="text-sm text-[#0A0A0A]">
                      I agree to list Glossop &amp; District Round Table as one of our funders and to promote Round Table on our website, marketing materials and social media where possible.
                    </span>
                  </label>
                  <div>
                    <FieldLabel>How do you intend to promote our organisation?</FieldLabel>
                    <textarea
                      data-testid="donation-promotion_detail"
                      rows={3}
                      value={form.promotion_detail}
                      onChange={update('promotion_detail')}
                      className="w-full bg-white border border-zinc-300 px-3 py-2 text-sm focus:border-[#F0A500] focus:outline-none focus:ring-1 focus:ring-[#F0A500]"
                    />
                  </div>
                  <label className="flex items-center gap-3" data-testid="donation-need_branding_assets-wrap">
                    <Checkbox checked={form.need_branding_assets} onCheckedChange={updateBool('need_branding_assets')} />
                    <span className="text-sm text-[#0A0A0A]">We&rsquo;d like Round Table to provide logos / wording</span>
                  </label>
                </div>
              </div>

              {/* Contact */}
              <div>
                <SectionHeader eyebrow="Step 4" title="Your contact details" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel required>Name</FieldLabel>
                    <Input data-testid="donation-contact_name" required value={form.contact_name} onChange={update('contact_name')} />
                  </div>
                  <div>
                    <FieldLabel required>Email</FieldLabel>
                    <Input data-testid="donation-contact_email" required type="email" value={form.contact_email} onChange={update('contact_email')} />
                  </div>
                  <div>
                    <FieldLabel required>Contact number</FieldLabel>
                    <Input data-testid="donation-contact_phone" required value={form.contact_phone} onChange={update('contact_phone')} />
                  </div>
                </div>
                <div className="mt-6">
                  <FieldLabel>Willing to volunteer at our events?</FieldLabel>
                  <p className="text-xs text-[#52525B] mb-3">Tick any of our events you&rsquo;d be happy to help out at &mdash; from general marshalling to bar work to take-down.</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <label className="flex items-center gap-3 p-3 bg-[#F4F4F5]" data-testid="donation-volunteer_heritage-wrap">
                      <Checkbox checked={form.volunteer_heritage} onCheckedChange={updateBool('volunteer_heritage')} />
                      <span className="text-sm">Heritage Weekend</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 bg-[#F4F4F5]" data-testid="donation-volunteer_glossfest-wrap">
                      <Checkbox checked={form.volunteer_glossfest} onCheckedChange={updateBool('volunteer_glossfest')} />
                      <span className="text-sm">GlossFest</span>
                    </label>
                    <label className="flex items-center gap-3 p-3 bg-[#F4F4F5]" data-testid="donation-volunteer_bonfire-wrap">
                      <Checkbox checked={form.volunteer_bonfire} onCheckedChange={updateBool('volunteer_bonfire')} />
                      <span className="text-sm">Glossop Bonfire</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Bank */}
              <div>
                <SectionHeader eyebrow="Step 5" title="Bank account details" blurb="So that we can pay any successful donation directly to your organisation. Treated as sensitive data and stored securely." />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>Bank name</FieldLabel>
                    <Input data-testid="donation-bank_name" value={form.bank_name} onChange={update('bank_name')} />
                  </div>
                  <div>
                    <FieldLabel>Account name</FieldLabel>
                    <Input data-testid="donation-account_name" value={form.account_name} onChange={update('account_name')} />
                  </div>
                  <div>
                    <FieldLabel>Account number</FieldLabel>
                    <Input data-testid="donation-account_number" inputMode="numeric" value={form.account_number} onChange={update('account_number')} />
                  </div>
                  <div>
                    <FieldLabel>Sort code</FieldLabel>
                    <Input data-testid="donation-sort_code" placeholder="XX-XX-XX" value={form.sort_code} onChange={update('sort_code')} />
                  </div>
                </div>
              </div>

              {/* Referral */}
              <div>
                <SectionHeader eyebrow="Step 6 (optional)" title="Know someone who'd love Round Table?" blurb="If they're male or identify as male, aged 18–45, and you'd like to recommend them to us, share their details with their permission." />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <FieldLabel>Name</FieldLabel>
                    <Input data-testid="donation-referral_name" value={form.referral_name} onChange={update('referral_name')} />
                  </div>
                  <div>
                    <FieldLabel>Age</FieldLabel>
                    <Input data-testid="donation-referral_age" type="number" inputMode="numeric" min="18" max="45" value={form.referral_age} onChange={update('referral_age')} />
                  </div>
                  <div>
                    <FieldLabel>Email</FieldLabel>
                    <Input data-testid="donation-referral_email" type="email" value={form.referral_email} onChange={update('referral_email')} />
                  </div>
                  <div>
                    <FieldLabel>Mobile number</FieldLabel>
                    <Input data-testid="donation-referral_phone" value={form.referral_phone} onChange={update('referral_phone')} />
                  </div>
                </div>
              </div>

              {/* GDPR */}
              <div>
                <SectionHeader eyebrow="Step 7" title="Data retention" />
                <div className="bg-[#F4F4F5] p-5 text-sm text-[#52525B] leading-relaxed space-y-3">
                  <p>
                    Under UK GDPR (General Data Protection Regulations 2018), we will only hold data pertinent to those we may donate to. All other data will be destroyed and not retained for future use. The data we collect will not be shared with any outside organisations, in the UK or internationally. We will not use the data for marketing purposes. If we wish to use any of the data held, we will request and acknowledge that with your organisation first.
                  </p>
                  <p>
                    If we are unable to donate to your organisation in this round but you&rsquo;d like us to retain your information for future use, tick the box below. Donations are generally made during Q4 of the financial year &mdash; though a time-sensitive request can be made at any time.
                  </p>
                </div>
                <label className="flex items-start gap-3 p-4 mt-4 border border-zinc-200" data-testid="donation-retain_data-wrap">
                  <Checkbox checked={form.retain_data} onCheckedChange={updateBool('retain_data')} />
                  <span className="text-sm text-[#0A0A0A]">
                    Please retain my information, for as long as is necessary, for future correspondence by Glossop Round Table.
                  </span>
                </label>
              </div>

              {error && <p data-testid="donation-form-error" className="text-sm text-red-600 font-semibold">{error}</p>}

              <div className="pt-6 border-t border-zinc-200">
                <label
                  className="flex items-center gap-3 p-4 bg-[#F4F4F5] border border-zinc-200 cursor-pointer select-none"
                  data-testid="donation-not_robot-wrap"
                >
                  <Checkbox
                    checked={notRobot}
                    onCheckedChange={(checked) => setNotRobot(checked === true)}
                    data-testid="donation-not_robot-checkbox"
                  />
                  <span className="text-sm font-semibold text-[#0A0A0A]">
                    I&rsquo;m not a robot
                  </span>
                </label>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
                <button
                  type="submit"
                  disabled={submitting || !notRobot}
                  data-testid="donation-submit-button"
                  className="px-8 py-3 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-60 disabled:cursor-not-allowed text-[#0A0A0A] font-bold uppercase tracking-wider text-sm transition-colors"
                >
                  {submitting ? 'Submitting…' : 'Submit donation request'}
                </button>
                <p className="text-xs text-[#52525B]">
                  Thank you for any community work you do and any volunteer services you provide for our events.
                </p>
              </div>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
